// 軌跡詳細画面
// 地図プレビュー(線で軌跡描画 + 写真ピン + POIピン) + 標高プロファイル + AI 振り返り

import { createMap, setupTrackLayer, updateTrack, maplibregl } from '../map.js';
import { showScreen, escapeHtml, fmtDate, fmtDistance, fmtDuration, toast, backToHome, showConfirm } from '../ui.js';
import { getTrack, deleteTrack, getPhotosByTrack, getAllPoi } from '../cache.js';
import { exportGpx, downloadBlob } from '../gpx.js';
import { profileToSvg } from '../elevation.js';
import { haversineMeters } from '../tracking.js';

let activeMap = null;
let markers = [];

const POI_KIND_LABELS = {
  water: '水',
  branch: '分',
  shelter: '小',
  view: '景',
  danger: '危',
  camp: 'C',
  note: 'メ',
};

export async function showTrackDetail(id) {
  cleanupTrackDetail();
  const t = await getTrack(id);
  if (!t) {
    toast('記録が見つかりません');
    return;
  }

  const stats = t.stats || {};
  const points = t.points || [];
  const photos = await getPhotosByTrack(t.id);
  const allPoi = await getAllPoi();
  // 軌跡の bounding box 内に入る POI を抽出
  const trackPoi = filterPoiInTrack(allPoi, points);

  // 標高プロファイル用 cumDist 計算
  const profile = [];
  let cum = 0;
  for (let i = 0; i < points.length; i++) {
    if (i > 0) cum += haversineMeters(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng);
    profile.push({ cumDist: cum, ele: points[i].ele });
  }
  let profileSvg = '';
  let profileMeta = '';
  if (profile.length >= 2 && profile.some((p) => typeof p.ele === 'number')) {
    const svg = profileToSvg(profile, 320, 120, 6);
    if (svg.path) {
      profileSvg = `
        <svg class="profile-chart" viewBox="0 0 320 120" xmlns="http://www.w3.org/2000/svg">
          <path d="${svg.area}" fill="rgba(10,10,10,0.06)"/>
          <path d="${svg.path}" fill="none" stroke="var(--ink)" stroke-width="1.5"/>
        </svg>
      `;
      profileMeta = `<p style="font-size:11px;color:var(--mute);margin-top:4px;font-family:var(--font-mono);letter-spacing:0.06em;">
        ${Math.round(svg.minEle)} m — ${Math.round(svg.maxEle)} m
      </p>`;
    }
  }

  const html = `
    <header class="screen-head">
      <button class="btn btn--icon" data-action="back" type="button" aria-label="戻る">
        <svg class="icon"><use href="#i-back"/></svg>
      </button>
      <h1 class="screen-head__title">${escapeHtml(t.name || '無題の記録')}</h1>
      <div class="screen-head__action">
        <button class="btn btn--icon" data-action="export-gpx" type="button" aria-label="GPX エクスポート">
          <svg class="icon"><use href="#i-share"/></svg>
        </button>
        <button class="btn btn--icon" data-action="delete-track" type="button" aria-label="削除">
          <svg class="icon"><use href="#i-trash"/></svg>
        </button>
      </div>
    </header>

    <p class="card__meta" style="margin-bottom:12px;">
      ${escapeHtml(fmtDate(new Date(t.startTs).toISOString()))} 開始 — ${escapeHtml(new Date(t.endTs).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }))} 終了
    </p>

    <div id="td-map" style="width:100%;height:280px;border:1px solid var(--line);margin-bottom:16px;"></div>

    <div class="detail-meta">
      <div class="detail-meta__cell">
        <p class="detail-meta__label">distance</p>
        <p class="detail-meta__value">${escapeHtml(fmtDistance(stats.distance || 0))}</p>
      </div>
      <div class="detail-meta__cell">
        <p class="detail-meta__label">time</p>
        <p class="detail-meta__value">${escapeHtml(fmtDuration(stats.duration || 0))}</p>
      </div>
      <div class="detail-meta__cell">
        <p class="detail-meta__label">elev gain</p>
        <p class="detail-meta__value">+${Math.round(stats.elevGain || 0)} m</p>
      </div>
      <div class="detail-meta__cell">
        <p class="detail-meta__label">elev loss</p>
        <p class="detail-meta__value">−${Math.round(stats.elevLoss || 0)} m</p>
      </div>
      <div class="detail-meta__cell">
        <p class="detail-meta__label">max elev</p>
        <p class="detail-meta__value">${stats.elevMax > -Infinity ? Math.round(stats.elevMax) + ' m' : '—'}</p>
      </div>
      <div class="detail-meta__cell">
        <p class="detail-meta__label">avg speed</p>
        <p class="detail-meta__value">${stats.avgSpeed ? (stats.avgSpeed * 3.6).toFixed(2) + ' km/h' : '—'}</p>
      </div>
    </div>

    ${profileSvg ? `
    <section class="section">
      <p class="section-label">elevation profile</p>
      ${profileSvg}
      ${profileMeta}
    </section>` : ''}

    <section class="section">
      <p class="section-label">points · 軌跡点</p>
      <p style="font-size:13px;color:var(--dim);">${points.length} 点を記録 · 写真 ${photos.length} 枚 · 地点メモ ${trackPoi.length} 件</p>
    </section>

    ${photos.length > 0 ? `
    <section class="section">
      <p class="section-label">photos · 撮影した写真</p>
      <div id="td-photos" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:6px;"></div>
    </section>` : ''}

    <section class="section">
      <button class="btn btn--ink btn--block" data-action="reflect" type="button">
        <svg class="icon"><use href="#i-sparkles"/></svg>
        <span>AI で振り返る(Claude Opus 5.5)</span>
      </button>
      <div id="reflection-body" style="margin-top:16px;"></div>
    </section>
  `;

  const screen = showScreen('track-detail', html);

  // 地図プレビュー初期化
  setTimeout(() => initMap(points, photos, trackPoi), 100);

  // 写真サムネイル
  if (photos.length > 0) {
    const grid = screen.querySelector('#td-photos');
    photos.forEach((p) => {
      const url = URL.createObjectURL(p.blob);
      const div = document.createElement('div');
      div.style.cssText = 'position:relative;aspect-ratio:1/1;overflow:hidden;border:1px solid var(--line);';
      div.innerHTML = `<img src="${url}" alt="登山写真" style="width:100%;height:100%;object-fit:cover;" />`;
      grid.appendChild(div);
    });
  }

  // ヘッダーアクション
  screen.querySelector('[data-action="export-gpx"]')?.addEventListener('click', () => {
    const gpxText = exportGpx(t);
    downloadBlob(new Blob([gpxText], { type: 'application/gpx+xml' }), `${t.name || 'track'}.gpx`);
    toast('GPX をダウンロードしました');
  });

  screen.querySelector('[data-action="delete-track"]')?.addEventListener('click', async () => {
    const ok = await showConfirm({
      title: '記録を削除',
      body: 'この登山記録を完全に削除します。元には戻せません。',
      confirmLabel: '削除する',
      destructive: true,
    });
    if (!ok) return;
    await deleteTrack(id);
    toast('削除しました');
    cleanupTrackDetail();
    backToHome();
  });

  screen.querySelector('[data-action="reflect"]')?.addEventListener('click', async () => {
    const btn = screen.querySelector('[data-action="reflect"]');
    const body = screen.querySelector('#reflection-body');
    btn.disabled = true;
    body.innerHTML = `<p style="font-size:13px;color:var(--dim);text-align:center;padding:16px;">AI が思考を整理しています…</p>`;
    try {
      const { generateReflection } = await import('../api.js');
      const data = await generateReflection(t, trackPoi);
      if (data.error) {
        body.innerHTML = `<p style="font-size:13px;color:var(--warn);">${escapeHtml(data.error)}</p>`;
        return;
      }
      const r = data.reflection;
      body.innerHTML = `
        <h3 style="font-family:var(--font-serif);font-size:18px;font-weight:400;margin:0 0 12px;">${escapeHtml(r.title || '')}</h3>
        <p class="section-label" style="margin-top:8px;">intent · なぜ歩いたか</p>
        <p style="font-family:var(--font-serif);font-size:14px;line-height:1.8;">${escapeHtml(r.intent || '')}</p>
        <p class="section-label" style="margin-top:16px;">experience · 歩いて何を感じたか</p>
        <p style="font-family:var(--font-serif);font-size:14px;line-height:1.8;">${escapeHtml(r.experience || '')}</p>
        <p class="section-label" style="margin-top:16px;">next · 次回への提案</p>
        <p style="font-family:var(--font-serif);font-size:14px;line-height:1.8;">${escapeHtml(r.next || '')}</p>
      `;
    } catch (e) {
      console.error(e);
      body.innerHTML = `<p style="font-size:13px;color:var(--warn);">通信に失敗しました: ${escapeHtml(e.message || '不明なエラー')}</p>`;
    } finally {
      btn.disabled = false;
    }
  });
}

function initMap(points, photos, pois) {
  if (points.length === 0) return;
  activeMap = createMap('td-map', {
    center: [points[0].lng, points[0].lat],
    zoom: 14,
    layer: 'pale',
    interactive: true,
  });
  setTimeout(() => { if (activeMap) activeMap.resize(); }, 200);

  activeMap.on('load', () => {
    activeMap.resize();
    setupTrackLayer(activeMap, 'track', '#0a0a0a', 4);
    const pts = points.map((p) => ({ lat: p.lat, lng: p.lng }));
    updateTrack(activeMap, 'track', pts);

    // 出発点 / 到達点マーカー
    if (points.length >= 2) {
      addEndpointMarker(activeMap, points[0], 'S');
      addEndpointMarker(activeMap, points[points.length - 1], 'G');
    }

    // 写真ピン
    photos.forEach((photo) => {
      if (typeof photo.lat !== 'number' || typeof photo.lng !== 'number') return;
      const el = document.createElement('div');
      el.style.cssText = `width:18px;height:18px;border-radius:50%;background:var(--bg);border:2px solid var(--ink);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--ink);cursor:pointer;`;
      el.innerHTML = '<svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h2l1.5-2h7L15 6h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z"/><circle cx="10" cy="11" r="3.5"/></svg>';
      const popupContent = `<img src="${URL.createObjectURL(photo.blob)}" alt="写真" style="width:200px;display:block;" />`;
      const m = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([photo.lng, photo.lat])
        .setPopup(new maplibregl.Popup({ closeButton: true, maxWidth: '220px' }).setHTML(popupContent))
        .addTo(activeMap);
      markers.push(m);
    });

    // POI ピン
    pois.forEach((poi) => {
      if (typeof poi.lat !== 'number' || typeof poi.lng !== 'number') return;
      const label = POI_KIND_LABELS[poi.kind] || '・';
      const el = document.createElement('div');
      el.style.cssText = `width:22px;height:22px;border-radius:4px;background:var(--warn);color:#fff;border:1.5px solid var(--bg);font-size:10px;font-family:var(--font-mono);display:flex;align-items:center;justify-content:center;font-weight:600;cursor:pointer;`;
      el.textContent = label;
      const popupContent = `<p style="margin:0 0 4px;font-weight:500;">${escapeHtml(poi.title || '')}</p>${poi.body ? `<p style="margin:0;font-size:12px;color:#666;">${escapeHtml(poi.body)}</p>` : ''}`;
      const m = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([poi.lng, poi.lat])
        .setPopup(new maplibregl.Popup({ closeButton: true, maxWidth: '220px' }).setHTML(popupContent))
        .addTo(activeMap);
      markers.push(m);
    });

    // 全体表示
    const bounds = new maplibregl.LngLatBounds();
    points.forEach((p) => bounds.extend([p.lng, p.lat]));
    activeMap.fitBounds(bounds, { padding: 40, duration: 400 });
  });
}

function addEndpointMarker(map, point, label) {
  const el = document.createElement('div');
  el.style.cssText = `width:22px;height:22px;border-radius:50%;background:var(--ink);color:var(--bg);border:2px solid var(--bg);font-size:11px;font-family:var(--font-mono);display:flex;align-items:center;justify-content:center;font-weight:600;`;
  el.textContent = label;
  const m = new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat([point.lng, point.lat])
    .addTo(map);
  markers.push(m);
}

// 軌跡 bbox 内の POI のみを返す(時系列で記録した地点)
function filterPoiInTrack(allPoi, points) {
  if (!points.length) return [];
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  points.forEach((p) => {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  });
  // 少し余裕を持たせる
  const pad = 0.005;
  return allPoi.filter((poi) =>
    poi.lat >= minLat - pad && poi.lat <= maxLat + pad &&
    poi.lng >= minLng - pad && poi.lng <= maxLng + pad
  );
}

export function cleanupTrackDetail() {
  markers.forEach((m) => m.remove());
  markers = [];
  if (activeMap) {
    activeMap.remove();
    activeMap = null;
  }
}
